import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput, TextArea } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface Capsule {
  id: string;
  itemTitle: string;
  whatWeSaw: string;
  soWeThink: string;
  weKnowRightIf: string;
  smallestSlice: string;
}

export default function EvidenceCapsulePage() {
  const [capsules, setCapsules] = useState<Capsule[]>([
    { id: '1', itemTitle: '', whatWeSaw: '', soWeThink: '', weKnowRightIf: '', smallestSlice: '' },
  ]);

  const updateCapsule = (id: string, field: keyof Capsule, value: string) => {
    setCapsules(capsules.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const addCapsule = () => {
    if (capsules.length < 10) {
      setCapsules([...capsules, { id: Date.now().toString(), itemTitle: '', whatWeSaw: '', soWeThink: '', weKnowRightIf: '', smallestSlice: '' }]);
    }
  };

  return (
    <TaskPage
      phase="gather"
      title="Evidence Capsule"
      pageNumber={4}
      instruction="For your top 10 items only: attach a single capsule each. Keep it tiny—fits a half-page card."
      whyItMatters="Closing mini-debriefs improve retention and set up later comparison; keep text short and measurable."
      coachRail={{
        timing: "15 minutes",
        tips: "Keep each capsule to 1-2 lines max. Focus on observable signals.",
        debrief: "Which capsules are strongest? Which need more evidence?"
      }}
    >
      <div className="space-y-6">
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4 mb-4">
          <p className="text-sm text-[var(--text-sec)]">
            <strong>For your top 10 items only.</strong> Focus on the most important options.
          </p>
        </div>

        {capsules.map((capsule, index) => (
          <div key={capsule.id} className="bg-[var(--surface)] p-6 border border-[var(--rule)]">
            <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Capsule {index + 1}</h3>
            <div className="space-y-4">
              <TextInput
                label="Item title"
                value={capsule.itemTitle}
                onChange={(e) => updateCapsule(capsule.id, 'itemTitle', e.target.value)}
                placeholder="Which item is this for?"
              />
              <TextArea
                label="What we saw / heard (1-2 lines)"
                value={capsule.whatWeSaw}
                onChange={(e) => updateCapsule(capsule.id, 'whatWeSaw', e.target.value)}
                placeholder="Quote or metric snapshot"
                rows={2}
              />
              <TextArea
                label="So we think (1 line)"
                value={capsule.soWeThink}
                onChange={(e) => updateCapsule(capsule.id, 'soWeThink', e.target.value)}
                placeholder="The hypothesis"
                rows={1}
              />
              <TextArea
                label="We'll know we're right if (1 line)"
                value={capsule.weKnowRightIf}
                onChange={(e) => updateCapsule(capsule.id, 'weKnowRightIf', e.target.value)}
                placeholder="Observable signal in ≤30 days"
                rows={1}
              />
              <TextArea
                label="Smallest slice to test (1 line)"
                value={capsule.smallestSlice}
                onChange={(e) => updateCapsule(capsule.id, 'smallestSlice', e.target.value)}
                placeholder="What we'd ship/try"
                rows={1}
              />
            </div>
          </div>
        ))}

        {capsules.length < 10 && (
          <button
            onClick={addCapsule}
            className="w-full py-2 px-4 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)] hover:text-[var(--text-sec)] transition-colors"
          >
            + Add another capsule (max 10)
          </button>
        )}

        <Callout type="tip" phase="gather">
          <p className="text-sm">Keep each capsule tiny—1-2 lines max. Focus on what's observable and measurable.</p>
        </Callout>
      </div>
    </TaskPage>
  );
}






